import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/ToastManager';
import { styles, COLORS } from '../styles/components/memberEditPageStyles';
import { ROUTES, NAVIGATION_OPTIONS } from '../config/routes';
import { PREFECTURES } from '../data/prefectures';
import {
  validatePrefecture,
  validatePostalCode,
  formatPostalCode 
} from '../utils/formValidation';
import useAddressSearch from '../hooks/useAddressSearch';
import PostalCodeInput from '../components/ui/PostalCodeInput';

/**
 * 会員情報編集ページコンポーネント
 */
const MemberEditPage = () => {
  const navigate = useNavigate();
  
  const { 
    memberData, 
    memberLoading,
    currentUser,
    refetchMemberData,
    getCsrfTokenFromCookie
  } = useAuth();
  
  const { showSuccess, showError } = useToast();
  
  // フォームの状態管理
  const [formData, setFormData] = useState({
    // 基本情報
    fullName: '',
    fullNameKana: '',
    phoneNumber: '',
    birthDate: '',
    gender: '',
    
    // 住所情報
    postalCode: '',
    prefecture: '',
    city: '',
    address: '',
    buildingRoom: '',
    
    // 緊急連絡先
    emergencyContactName: '',
    emergencyContactPhone: ''
  });
  
  const [activeTab, setActiveTab] = useState('basic');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [csrfStatus, setCsrfStatus] = useState({ loading: false, error: null });

  // 会員データが読み込まれたらフォームを初期化
  useEffect(() => {
    if (memberData) {
      setFormData({
        fullName: memberData.fullName || '',
        fullNameKana: memberData.fullNameKana || '',
        phoneNumber: memberData.phoneNumber || '',
        birthDate: memberData.birthDate || '',
        gender: memberData.gender || '',
        postalCode: memberData.postalCode || '',
        prefecture: memberData.prefecture || '',
        city: memberData.city || '',
        address: memberData.address || '',
        buildingRoom: memberData.buildingRoom || '',
        emergencyContactName: memberData.emergencyContactName || '',
        emergencyContactPhone: memberData.emergencyContactPhone || ''
      });
    }
  }, [memberData]);

  const validateForm = (tabName) => {
    const errors = {};
    
    // 基本情報タブのバリデーション
    if (tabName === 'basic') {
      validateBasicTab(errors);
      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    }
    
    // 住所情報タブのバリデーション
    if (tabName === 'address') {
      validateAddressTab(errors);
      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    }
    
    // 未知のタブ名の場合はエラーなしとして処理
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * 基本情報タブのバリデーション
   * @param {Object} errors - エラーオブジェクト（参照で更新）
   */
  const validateBasicTab = (errors) => {
    // 氏名のバリデーション
    if (!formData.fullName.trim()) {
      errors.fullName = '氏名は必須です';
    }
    
    // ひらがな名前のバリデーション
    if (!formData.fullNameKana.trim()) {
      errors.fullNameKana = 'ひらがな名前は必須です';
      return; // 空の場合は形式チェックをスキップ
    }
    
    if (!/^[あ-ん゛゜\s　]*$/.test(formData.fullNameKana)) {
      errors.fullNameKana = 'ひらがなで入力してください';
    }
    
    // 電話番号のバリデーション
    if (!formData.phoneNumber.trim()) {
      errors.phoneNumber = '電話番号は必須です';
    }
  };

  /**
   * 住所情報タブのバリデーション
   * @param {Object} errors - エラーオブジェクト（参照で更新）
   */
  const validateAddressTab = (errors) => {
    // 郵便番号のバリデーション
    if (!formData.postalCode.trim()) {
      errors.postalCode = '郵便番号は必須です';
    } else {
      const postalError = validatePostalCode(formData.postalCode);
      if (postalError) {
        errors.postalCode = postalError;
      }
    }
    
    // 都道府県のバリデーション
    if (!formData.prefecture.trim()) {
      errors.prefecture = '都道府県は必須です';
    }
    const prefectureError = formData.prefecture.trim() ? validatePrefecture(formData.prefecture) : null;
    if (prefectureError) {
      errors.prefecture = prefectureError;
    }
    
    // 市区町村のバリデーション
    if (!formData.city.trim()) {
      errors.city = '市区町村は必須です';
    }
    
    // 住所のバリデーション
    if (!formData.address.trim()) {
      errors.address = '住所は必須です';
    }
  };

  const { postalMessage, isSuccess, searchAddress } = useAddressSearch(({ prefectureLabel, city, address }) => {
    setFormData(prev => ({ ...prev, prefecture: prefectureLabel, city, address }));
  });

  // API呼び出し用の更新関数
  const updateMemberInfo = async (updateData) => {
    if (!currentUser) {
      throw new Error('認証が必要です');
    }

    try {
      
      // CSRFトークンを取得
      const csrfToken = await getCsrfTokenFromCookie(true);
      
      if (!csrfToken) {
        throw new Error('セキュリティトークンの取得に失敗しました');
      }

      const response = await fetch('/api/me', {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken
        },
        body: JSON.stringify(updateData)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || '更新に失敗しました');
      }

      return { success: true, message: result.message };

    } catch (error) {
      console.error('[MEMBER EDIT] Failed to update member info:', error);

      // CSRFトークン取得失敗の場合の特別な処理
      if (error.message.includes('セキュリティトークン')) {
        throw new Error('セキュリティトークンの取得に失敗しました。ページを再読み込みして再度お試しください。');
      }
      
      // CSRF関連エラーの特別な処理
      if (error.message.includes('CSRF') || error.message.includes('セキュリティ')) {
        throw new Error('セキュリティ検証エラーが発生しました。ページを再読み込みして再度お試しください。');
      }
      
      throw error;
    }
  };

  // フォーム送信処理
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm(activeTab)) {
      showError('入力内容を確認してください');
      return;
    }

    setIsSubmitting(true);
    setCsrfStatus({ loading: true, error: null });
    
    try {
      let updateData = {};
      
      switch (activeTab) {
        case 'basic':
          updateData = {
            fullName: formData.fullName,
            fullNameKana: formData.fullNameKana,
            phoneNumber: formData.phoneNumber,
            birthDate: formData.birthDate,
            gender: formData.gender
          };
          break;
          
        case 'address':
          updateData = {
            postalCode: formData.postalCode,
            prefecture: formData.prefecture,
            city: formData.city,
            address: formData.address,
            buildingRoom: formData.buildingRoom
         };
          break;
          
        case 'emergency':
          updateData = {
            emergencyContactName: formData.emergencyContactName,
            emergencyContactPhone: formData.emergencyContactPhone
          };
          break;
          
        default:
          throw new Error('不正なタブです');
      }

      const result = await updateMemberInfo(updateData);

      showSuccess(result.message || '会員情報を更新しました');

      // 会員データを再取得
      if (refetchMemberData) {
        await refetchMemberData();
      }

      setCsrfStatus({ loading: false, error: null });

      // ホーム画面に戻る（成功メッセージ付き）
      navigate(ROUTES.HOME, NAVIGATION_OPTIONS.REPLACE);
        
    } catch (error) {
      console.error('[MEMBER EDIT] Update failed:', error);
      setCsrfStatus({ loading: false, error: error.message });
      
      // CSRF関連エラーの場合は専用メッセージ
      if (error.message.includes('CSRF') || error.message.includes('セキュリティ')) {
        showError('セキュリティ検証エラーが発生しました。ページを再読み込みして再度お試しください。');
      } else {
        showError(error.message || '更新処理でエラーが発生しました');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // 入力値変更ハンドラー
  const handleInputChange = (field, value) => {
    // 郵便番号の場合は自動フォーマットを適用（共通関数を使用）
    const formattedValue = field === 'postalCode' ? formatPostalCode(value) : value;

    setFormData(prev => ({
      ...prev,
      [field]: formattedValue
    }));
    
    // バリデーションエラーをクリア
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // 戻るボタンの処理
  const handleBack = () => {
    navigate(ROUTES.HOME);
  };

  // 入力フィールドのレンダリング関数
  const renderInputField = (name, label, required = false, inputProps = {}) => {
    const hasError = validationErrors[name];
    
    return (
      <tr key={name} className="member-edit-table-row">
        <th style={styles.th} className="member-edit-table-th">
          <span>{label}</span>
          {required && <span style={styles.required}> *</span>}
        </th>
        <td style={styles.td} className="member-edit-table-td">
          <input
            type="text"
            name={name}
            value={formData[name]}
            onChange={(e) => handleInputChange(name, e.target.value)}
            style={{
              ...styles.input,
              ...(hasError ? styles.inputError : {})
            }}
            disabled={isSubmitting}
            {...inputProps}
          />
          {hasError && (
            <div style={styles.error}>{hasError}</div>
          )}
        </td>
      </tr>
    );
  };

  // セレクトフィールドのレンダリング関数
  const renderSelectField = (name, label, options, required = false) => {
    return (
      <tr key={name} className="member-edit-table-row">
        <th style={styles.th} className="member-edit-table-th">
          <span>{label}</span>
          {required && <span style={styles.required}> *</span>}
        </th>
        <td style={styles.td} className="member-edit-table-td">
          <select
            name={name}
            value={formData[name]}
            onChange={(e) => handleInputChange(name, e.target.value)}
            style={styles.select}
            disabled={isSubmitting}
          >
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </td>
      </tr>
    );
  };

  // タブコンテンツのレンダリング
  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <table style={styles.table} className="member-edit-table">
            <tbody>
              {renderInputField('fullName', '氏名', true)}
              {renderInputField('fullNameKana', 'ひらがな名前', true)}
              {renderInputField('phoneNumber', '電話番号', true, { type: 'tel' })}
              {renderInputField('birthDate', '生年月日', false, { type: 'date' })}
              {renderSelectField('gender', '性別', [
                { value: '', label: '選択してください' },
                { value: '男性', label: '男性' },
                { value: '女性', label: '女性' },
                { value: 'その他', label: 'その他' }
              ])}
            </tbody>
          </table>
        );
        
      case 'address':
        return (
          <table style={styles.table} className="member-edit-table">
            <tbody>
              {/* 郵便番号 - 住所検索機能付き */}
              <PostalCodeInput
                label="郵便番号"
                required
                name="postalCode"
                value={formData.postalCode}
                onChange={(e) => handleInputChange('postalCode', e.target.value)}
                onSearch={searchAddress}
                error={validationErrors.postalCode}
                postalMessage={postalMessage}
                isSuccess={isSuccess}
                disabled={isSubmitting}
                thStyle={styles.th}
                tdStyle={styles.td}
                inputStyle={{ ...styles.input, ...(validationErrors.postalCode ? styles.inputError : {}) }}
                errorStyle={styles.error}
                requiredStyle={styles.required}
                rowClassName="member-edit-table-row"
                thClassName="member-edit-table-th"
                tdClassName="member-edit-table-td"
              />
              
              {renderSelectField('prefecture', '都道府県', 
                PREFECTURES.map(p => ({ value: p.label, label: p.label })),
                true
              )}
              {renderInputField('city', '市区町村', true)}
              {renderInputField('address', '住所', true)}
              {renderInputField('buildingRoom', '建物・部屋番号', false)}
            </tbody>
          </table>
        );
        
      case 'emergency':
        return (
          <table style={styles.table} className="member-edit-table">
            <tbody>
              {renderInputField('emergencyContactName', '緊急連絡先氏名', false)}
              {renderInputField('emergencyContactPhone', '緊急連絡先電話番号', false, { type: 'tel' })}
            </tbody>
          </table>
        );
        
      default:
        return null;
    }
  };

  if (memberLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.loadingMessage}>読み込み中...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container} className="member-edit-container">
      <div style={styles.content} className="member-edit-content">
        {/* ヘッダー */}
        <div style={styles.header} className="member-edit-header">
          <h1 style={styles.title} className="member-edit-title">会員情報編集</h1>
          <div>
            <button 
              style={styles.backButton} 
              className="member-edit-back-button"
              onClick={handleBack}
              onMouseOver={(e) => e.target.style.backgroundColor = COLORS.primaryDark}
              onMouseOut={(e) => e.target.style.backgroundColor = '#09529f'}
            >
              ← ホームに戻る
            </button>
          </div>
        </div>

        {/* CSRF統合ステータス表示 */}
        {csrfStatus.loading && (
          <div style={{...styles.csrfStatus, ...styles.csrfStatusLoading}}>
            <strong>🔒 CSRF保護:</strong><span> セキュリティトークンを取得中...</span>
          </div>
        )}

        {csrfStatus.error && (
          <div style={{...styles.csrfStatus, ...styles.csrfStatusError}}>
            <strong>⚠️ CSRF保護エラー:</strong><span>{` ${csrfStatus.error}`}</span>
          </div>
        )}
        
        {/* タブナビゲーション */}
        <div style={styles.tabContainer} className="member-edit-tab-container">
          {[
            { key: 'basic', label: '基本情報' },
            { key: 'address', label: '住所情報' },
            { key: 'emergency', label: '緊急連絡先' }
          ].map(tab => (
            <button
              key={tab.key}
              style={{
                ...styles.tab,
                ...(activeTab === tab.key ? styles.activeTab : {})
              }}
              className="member-edit-tab"
              onClick={() => setActiveTab(tab.key)}
              onMouseOver={(e) => {
                if (activeTab !== tab.key) {
                  e.target.style.color = COLORS.primary;
                }
              }}
              onMouseOut={(e) => {
                if (activeTab !== tab.key) {
                  e.target.style.color = COLORS.textGray;
                }
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* フォーム */}
        <form onSubmit={handleSubmit}>
          <div style={styles.formSection}>
            {renderTabContent()}
          </div>
          
          {/* ボタンエリア */}
          <div style={styles.buttonContainer} className="member-edit-button-container">
            <button
              type="button"
              style={{...styles.button, ...styles.secondaryButton}}
              className="member-edit-button"
              onClick={handleBack}
              disabled={isSubmitting}
              onMouseOver={(e) => !isSubmitting && (e.target.style.backgroundColor = COLORS.primaryDark)}
              onMouseOut={(e) => !isSubmitting && (e.target.style.backgroundColor = COLORS.textGray)}
            >
              キャンセル
            </button>
            <button
              type="submit"
              style={{
                ...styles.button, 
                ...styles.primaryButton,
                opacity: isSubmitting ? 0.6 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}
              className="member-edit-button"
              disabled={isSubmitting}
              onMouseOver={(e) => !isSubmitting && (e.target.style.backgroundColor = COLORS.primaryDark)}
              onMouseOut={(e) => !isSubmitting && (e.target.style.backgroundColor = COLORS.primary)}
            >
              {isSubmitting ? '更新中...' : '更新'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MemberEditPage;